import { useState } from "react";
import { toast } from "sonner";
import { X as XIcon, Download, FileText, FileSpreadsheet, FileType2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ExportDialog — choose format + customisation for a single task list export.
 * Renders a download trigger that hits /api/exports/list/{listId}?format=...
 */
export default function ExportDialog({ list, open, onClose }) {
  const [format, setFormat] = useState("pdf");
  const [opts, setOpts] = useState({
    include_subtasks: true,
    include_completed: false,
    include_priority: true,
    include_due_date: true,
    include_description: false,
  });
  const [busy, setBusy] = useState(false);

  if (!open || !list) return null;

  const doDownload = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ format, ...Object.fromEntries(Object.entries(opts).map(([k, v]) => [k, String(v)])) });
      const url = `${API}/exports/list/${list.id}?${params.toString()}`;
      // Use fetch with credentials so the auth cookie/header is sent, then trigger a save
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const disp = res.headers.get("Content-Disposition") || "";
      const m = /filename="?([^"]+)"?/.exec(disp);
      const filename = m ? m[1] : `${list.name || "list"}.${format}`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Export ready");
      onClose();
    } catch (e) {
      toast.error("Export failed: " + (e.message || "unknown"));
    } finally {
      setBusy(false);
    }
  };

  const formats = [
    { value: "pdf", label: "PDF", Icon: FileType2, desc: "Print-ready document" },
    { value: "docx", label: "Word", Icon: FileText, desc: "Editable .docx" },
    { value: "xlsx", label: "Excel", Icon: FileSpreadsheet, desc: "Spreadsheet with subtask sheet" },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="export-dialog">
      <div className="bg-white border border-zinc-200 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <header className="border-b border-zinc-200 px-5 py-4 flex items-center justify-between">
          <h2 className="text-base font-semibold tracking-tight flex items-center gap-2">
            <Download className="w-4 h-4 text-[#002FA7]" strokeWidth={1.75} />
            Export "{list.name}"
          </h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900" aria-label="Close" data-testid="export-close">
            <XIcon className="w-4 h-4" />
          </button>
        </header>

        <div className="px-5 py-4 space-y-5">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2 block">Format</label>
            <div className="grid grid-cols-3 gap-1.5">
              {formats.map(({ value, label, Icon, desc }) => (
                <button
                  type="button"
                  key={value}
                  onClick={() => setFormat(value)}
                  className={`text-left p-3 border transition-colors ${format === value ? "border-[#002FA7] bg-blue-50" : "border-zinc-200 hover:border-zinc-400"}`}
                  data-testid={`export-format-${value}`}
                >
                  <div className="flex items-center gap-1.5">
                    <Icon className="w-3.5 h-3.5 text-zinc-600" strokeWidth={1.5} />
                    <span className="text-xs font-semibold">{label}</span>
                  </div>
                  <p className="text-[10px] text-zinc-500 mt-1 leading-tight">{desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2 block">Include</label>
            <div className="space-y-2">
              {[
                ["include_subtasks", "Subtasks"],
                ["include_priority", "Priority"],
                ["include_due_date", "Due dates"],
                ["include_completed", "Completed tasks"],
                ["include_description", "Description"],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer text-sm text-zinc-700" data-testid={`export-toggle-${key}`}>
                  <input
                    type="checkbox"
                    checked={opts[key]}
                    onChange={(e) => setOpts((o) => ({ ...o, [key]: e.target.checked }))}
                    className="accent-[#002FA7] w-4 h-4"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
        </div>

        <footer className="px-5 py-4 border-t border-zinc-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs uppercase tracking-[0.12em] px-3 py-2 text-zinc-600 hover:bg-zinc-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={doDownload}
            disabled={busy}
            className="text-xs uppercase tracking-[0.12em] px-4 py-2 bg-zinc-900 text-white hover:bg-[#002FA7] disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="export-download-btn"
          >
            {busy ? <><Loader2 className="w-3 h-3 animate-spin" />Generating…</> : <><Download className="w-3 h-3" />Download</>}
          </button>
        </footer>
      </div>
    </div>
  );
}
